import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Eye, EyeOff, Check, Trash2, RotateCcw, Save } from "lucide-react";
import {
  adminLogin,
  verifyAdminSession,
  getOurCowmunity,
  updateOurCowmunity,
  getOurCowmunityDirectory,
} from "../lib/api";

const STORAGE_KEY = "salty-moo-admin";

const inputCls =
  "w-full px-4 py-3 rounded-md bg-[#F9F6F0] border border-[#E7DFD5] focus:outline-none focus:border-[#4B3A2F] font-body text-[#2C2724]";
const labelCls =
  "text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2 block";

/** Small labelled input row, matches the rest of Admin. */
function Field({ label, value, onChange, textarea, rows = 3, testId }) {
  return (
    <div className="mb-5">
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea
          data-testid={testId}
          className={inputCls + " min-h-[80px]"}
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          data-testid={testId}
          type="text"
          className={inputCls}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function VisibilityRow({ label, value, onToggle, testId }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-[#2C2724]">{label}</span>
      <button
        type="button"
        data-testid={testId}
        onClick={onToggle}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs transition-colors ${
          value
            ? "border-[#7E9268] text-[#7E9268] hover:bg-[#7E9268] hover:text-white"
            : "border-[#A87149] text-[#A87149] hover:bg-[#A87149] hover:text-white"
        }`}
      >
        {value ? <Eye size={12} /> : <EyeOff size={12} />}
        {value ? "Visible" : "Hidden"}
      </button>
    </div>
  );
}

export default function AdminCowmunity() {
  const [passcode, setPasscode] = useState(
    () => localStorage.getItem(STORAGE_KEY) || "",
  );
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    if (!passcode) return;
    verifyAdminSession(passcode)
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        setPasscode("");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [passInput, setPassInput] = useState("");
  const [authBusy, setAuthBusy] = useState(false);

  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dir, setDir] = useState(null);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (!authed) return;
    getOurCowmunity().then(setData).catch(() => setData({}));
    getOurCowmunityDirectory()
      .then(setDir)
      .catch(() => setDir({ cards: [], count: 0 }));
  }, [authed]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    try {
      const res = await adminLogin(passInput.trim());
      if (res.ok) {
        localStorage.setItem(STORAGE_KEY, res.token);
        setPasscode(res.token);
        setAuthed(true);
      }
    } catch (err) {
      toast(err?.response?.status === 429 ? err.response.data.detail : "Incorrect passcode");
    } finally {
      setAuthBusy(false);
    }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async (patch = null) => {
    setSaving(true);
    try {
      const payload = patch || data;
      const res = await updateOurCowmunity(payload, passcode);
      setData(res);
      toast("Saved");
    } catch (err) {
      toast("Couldn\u2019t save", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const uploadPasturePhoto = async (slot, file) => {
    if (!file) return;
    setUploading(`slot-${slot}`);
    try {
      const form = new FormData();
      form.append("file", file);
      // Reuse the generic photo upload endpoint for a slot-based slug.
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/our-cow-munity/pasture-photo/${slot}`,
        {
          method: "POST",
          headers: { "x-admin-passcode": passcode },
          body: form,
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      // Refetch so the local `data` reflects the new URL and any other fields.
      const fresh = await getOurCowmunity();
      setData(fresh);
      toast("Photo saved");
    } catch (err) {
      toast("Photo upload failed", { description: err.message });
    } finally {
      setUploading("");
    }
  };

  const toggleHideRow = async (rowIdx) => {
    const cur = new Set(data.hidden_rows || []);
    if (cur.has(rowIdx)) cur.delete(rowIdx);
    else cur.add(rowIdx);
    const hidden_rows = Array.from(cur).sort((a, b) => a - b);
    await save({ hidden_rows });
    getOurCowmunityDirectory().then(setDir).catch(() => {});
  };

  // -------- login gate --------
  if (!authed) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center px-6">
        <form
          onSubmit={submitLogin}
          data-testid="admin-cowmunity-login"
          className="parchment-deep page-shadow max-w-md w-full p-10 rounded-md border border-[#E7DFD5] text-center"
        >
          <h1 className="font-display text-3xl text-[#4B3A2F] mb-2">
            Our Cow-munity Manager
          </h1>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            tend the trail
          </p>
          <label className={labelCls}>Admin passcode</label>
          <input
            data-testid="admin-cowmunity-passcode-input"
            type="password"
            className={inputCls}
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            autoFocus
          />
          <button
            type="submit"
            disabled={authBusy || !passInput}
            data-testid="admin-cowmunity-login-btn"
            className="btn-pasture mt-5 w-full justify-center"
          >
            {authBusy ? "Opening..." : "Open the trail"}
          </button>
        </form>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center">
        <p className="text-sm text-[#6D5A50] italic">{"loading\u2026"}</p>
      </div>
    );
  }

  return (
    <div
      className="parchment min-h-screen pt-20 pb-24 px-6"
      data-testid="admin-cowmunity-page"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
          <div className="flex items-baseline gap-3">
            <Users size={22} className="text-[#4B3A2F]" />
            <h1 className="font-display text-4xl text-[#4B3A2F]">
              Our Cow-munity Manager
            </h1>
          </div>
          <Link
            to="/admin"
            data-testid="admin-cowmunity-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#4B3A2F] hover:text-[#D62839] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Admin
          </Link>
        </div>
        <p className="font-script text-2xl text-[#4B3A2F] mb-8">
          tend the trail &mdash; every field on the page is editable here
        </p>

        {/* Page header copy */}
        <section
          data-testid="cowmunity-editor-header"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-4"
        >
          <h2 className="font-display text-2xl text-[#4B3A2F] mb-1">
            Page header
          </h2>
          <p className="font-script text-lg text-[#4B3A2F] mb-6">
            the first cedar sign visitors see
          </p>
          <Field
            label="Page title (cedar sign)"
            value={data.page_title}
            onChange={(v) => set("page_title", v)}
            testId="cow-page-title"
          />
          <Field
            label="SEO title (browser tab / search results)"
            value={data.seo_title}
            onChange={(v) => set("seo_title", v)}
            testId="cow-seo-title"
          />
          <Field
            label="SEO description"
            value={data.seo_description}
            onChange={(v) => set("seo_description", v)}
            textarea
            rows={2}
            testId="cow-seo-description"
          />
          <Field
            label="Hero title (over the pasture image)"
            value={data.hero_title}
            onChange={(v) => set("hero_title", v)}
            testId="cow-hero-title"
          />
          <Field
            label="Hero subtitle"
            value={data.hero_subtitle}
            onChange={(v) => set("hero_subtitle", v)}
            testId="cow-hero-subtitle"
          />
          <Field
            label="Intro paragraph"
            value={data.intro_body}
            onChange={(v) => set("intro_body", v)}
            textarea
            rows={6}
            testId="cow-intro-body"
          />
          <button
            className="btn-pasture inline-flex items-center gap-2"
            onClick={() => save()}
            disabled={saving}
            data-testid="cow-save-header"
          >
            <Save size={14} />
            {saving ? "Saving\u2026" : "Save"}
          </button>
        </section>

        {/* Salt Trail */}
        <section
          data-testid="cowmunity-editor-salt-trail"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl text-[#4B3A2F] mb-1">
                The Salt Trail
              </h2>
              <p className="font-script text-lg text-[#4B3A2F]">
                the directory + Google Form
              </p>
            </div>
            <VisibilityRow
              label=""
              value={data.show_salt_trail !== false}
              onToggle={() =>
                save({ show_salt_trail: !(data.show_salt_trail !== false) })
              }
              testId="cow-toggle-salt-trail"
            />
          </div>
          <Field
            label="Section heading (cedar sign)"
            value={data.salt_trail_heading}
            onChange={(v) => set("salt_trail_heading", v)}
            testId="cow-salt-heading"
          />
          <Field
            label="Sub-sign text"
            value={data.salt_trail_sign}
            onChange={(v) => set("salt_trail_sign", v)}
            testId="cow-salt-sign"
          />
          <Field
            label="Tagline (script)"
            value={data.salt_trail_tagline}
            onChange={(v) => set("salt_trail_tagline", v)}
            testId="cow-salt-tagline"
          />
          <Field
            label="Intro paragraph"
            value={data.salt_trail_body}
            onChange={(v) => set("salt_trail_body", v)}
            textarea
            rows={6}
            testId="cow-salt-body"
          />
          <Field
            label="Button label"
            value={data.button_text}
            onChange={(v) => set("button_text", v)}
            testId="cow-button-text"
          />
          <Field
            label="Google Form URL (public /viewform link)"
            value={data.form_url}
            onChange={(v) => set("form_url", v)}
            testId="cow-form-url"
          />
          <Field
            label="Published Google Sheet URL (/pubhtml link)"
            value={data.sheet_pub_url}
            onChange={(v) => set("sheet_pub_url", v)}
            testId="cow-sheet-url"
          />
          <Field
            label="Card footer text"
            value={data.card_footer}
            onChange={(v) => set("card_footer", v)}
            testId="cow-card-footer"
          />
          <Field
            label="Browse heading (above the search box)"
            value={data.browse_heading}
            onChange={(v) => set("browse_heading", v)}
            testId="cow-browse-heading"
          />
          <Field
            label="Browse tagline (script line)"
            value={data.browse_tagline}
            onChange={(v) => set("browse_tagline", v)}
            testId="cow-browse-tagline"
          />
          <Field
            label="Search placeholder"
            value={data.search_placeholder}
            onChange={(v) => set("search_placeholder", v)}
            testId="cow-search-placeholder"
          />
          <Field
            label="Empty trail message (when no cards exist yet)"
            value={data.empty_trail_message}
            onChange={(v) => set("empty_trail_message", v)}
            testId="cow-empty-trail"
          />
          <Field
            label="No-matches message (when search returns nothing)"
            value={data.no_matches_message}
            onChange={(v) => set("no_matches_message", v)}
            testId="cow-no-matches"
          />
          <Field
            label="Next-page button label"
            value={data.cowmunity_next_button_label}
            onChange={(v) => set("cowmunity_next_button_label", v)}
            testId="cow-next-button"
          />
          <button
            className="btn-pasture inline-flex items-center gap-2"
            onClick={() => save()}
            disabled={saving}
            data-testid="cow-save-salt-trail"
          >
            <Save size={14} />
            {saving ? "Saving\u2026" : "Save"}
          </button>
        </section>

        {/* Directory approval */}
        <section
          data-testid="cowmunity-editor-directory"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-8"
        >
          <h2 className="font-display text-2xl text-[#4B3A2F] mb-1">
            Directory cards
          </h2>
          <p className="font-script text-lg text-[#4B3A2F] mb-4">
            hide or restore individual submissions
          </p>
          <p className="text-sm text-[#6D5A50] italic mb-6">
            {dir
              ? `${dir.count || 0} cards currently visible along The Salt Trail. ${
                  (data.hidden_rows || []).length
                } hidden.`
              : "loading\u2026"}
          </p>
          {dir?.cards?.length > 0 ? (
            <div className="max-h-96 overflow-y-auto pr-2 space-y-2">
              {dir.cards.map((card) => (
                <div
                  key={card.row}
                  className="flex items-center justify-between py-2 border-b border-[#E7DFD5]/70 last:border-0"
                >
                  <div className="min-w-0 flex-1 mr-3">
                    <p className="font-display text-sm text-[#4B3A2F] truncate">
                      {card.farm_name}
                    </p>
                    <p className="text-xs text-[#6D5A50] truncate">
                      {card.location}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`cow-hide-row-${card.row}`}
                    onClick={() => toggleHideRow(card.row)}
                    className="p-2 rounded-md border border-[#C8A876] text-[#8D6E63] hover:bg-[#C8A876] hover:text-[#2C2724] transition-colors"
                    title="Hide from public"
                  >
                    <EyeOff size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {(data.hidden_rows || []).length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2">
                Currently hidden
              </p>
              <div className="flex flex-wrap gap-2">
                {(data.hidden_rows || []).map((r) => (
                  <button
                    key={r}
                    type="button"
                    data-testid={`cow-restore-row-${r}`}
                    onClick={() => toggleHideRow(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#7E9268] text-[#7E9268] text-xs hover:bg-[#7E9268] hover:text-white transition-colors"
                  >
                    <RotateCcw size={12} /> Row {r + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Around the Pasture */}
        <section
          data-testid="cowmunity-editor-pasture"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl text-[#4B3A2F] mb-1">
                Around the Pasture
              </h2>
              <p className="font-script text-lg text-[#4B3A2F]">
                the four photos + copy
              </p>
            </div>
            <VisibilityRow
              label=""
              value={data.show_pasture !== false}
              onToggle={() =>
                save({ show_pasture: !(data.show_pasture !== false) })
              }
              testId="cow-toggle-pasture"
            />
          </div>
          <Field
            label="Section heading"
            value={data.pasture_heading}
            onChange={(v) => set("pasture_heading", v)}
            testId="cow-pasture-heading"
          />
          <Field
            label="Section body (script line)"
            value={data.pasture_body}
            onChange={(v) => set("pasture_body", v)}
            testId="cow-pasture-body"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {[1, 2, 3, 4].map((n) => {
              const url = data[`pasture_photo_${n}`];
              return (
                <div
                  key={n}
                  className="text-center"
                  data-testid={`cow-pasture-slot-${n}`}
                >
                  <div className="aspect-square bg-[#EFE4CE] border border-[#E7DFD5] rounded-md overflow-hidden mb-2">
                    {url ? (
                      <img
                        src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#8D6E63] font-script">
                        #{n}
                      </div>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#7E9268] text-[#7E9268] text-xs cursor-pointer hover:bg-[#7E9268] hover:text-white transition-colors">
                    {uploading === `slot-${n}` ? "\u2026" : url ? "Replace" : "Upload"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`cow-pasture-upload-${n}`}
                      onChange={(e) =>
                        uploadPasturePhoto(n, e.target.files?.[0])
                      }
                    />
                  </label>
                  {url && (
                    <button
                      type="button"
                      data-testid={`cow-pasture-clear-${n}`}
                      onClick={() => save({ [`pasture_photo_${n}`]: "" })}
                      className="ml-2 p-1.5 rounded-md border border-[#D62839] text-[#D62839] hover:bg-[#D62839] hover:text-white transition-colors align-middle"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Caught Something Moo-velous */}
        <section
          data-testid="cowmunity-editor-moovelous"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl text-[#4B3A2F] mb-1">
                Caught Something Moo-velous?
              </h2>
              <p className="font-script text-lg text-[#4B3A2F]">
                photo submission call-out
              </p>
            </div>
            <VisibilityRow
              label=""
              value={data.show_moovelous !== false}
              onToggle={() =>
                save({ show_moovelous: !(data.show_moovelous !== false) })
              }
              testId="cow-toggle-moovelous"
            />
          </div>
          <Field
            label="Section heading"
            value={data.moovelous_heading}
            onChange={(v) => set("moovelous_heading", v)}
            testId="cow-moo-heading"
          />
          <Field
            label="Body copy"
            value={data.moovelous_body}
            onChange={(v) => set("moovelous_body", v)}
            textarea
            rows={7}
            testId="cow-moo-body"
          />
          <Field
            label="Contact email"
            value={data.moovelous_email}
            onChange={(v) => set("moovelous_email", v)}
            testId="cow-moo-email"
          />
          <Field
            label="SEO title"
            value={data.moovelous_seo_title}
            onChange={(v) => set("moovelous_seo_title", v)}
            testId="cow-moo-seo-title"
          />
          <Field
            label="SEO description"
            value={data.moovelous_seo_description}
            onChange={(v) => set("moovelous_seo_description", v)}
            textarea
            rows={2}
            testId="cow-moo-seo-description"
          />
          <Field
            label="Hero subtitle"
            value={data.moovelous_hero_subtitle}
            onChange={(v) => set("moovelous_hero_subtitle", v)}
            testId="cow-moo-hero-subtitle"
          />
          <Field
            label="Empty photo placeholder label"
            value={data.moovelous_empty_photo_label}
            onChange={(v) => set("moovelous_empty_photo_label", v)}
            testId="cow-moo-empty-label"
          />
          <Field
            label="Closing script line (under the photo gallery)"
            value={data.moovelous_closing_line}
            onChange={(v) => set("moovelous_closing_line", v)}
            testId="cow-moo-closing"
          />
          <Field
            label="Next-page button label"
            value={data.moovelous_next_button_label}
            onChange={(v) => set("moovelous_next_button_label", v)}
            testId="cow-moo-next-button"
          />
          <button
            className="btn-pasture inline-flex items-center gap-2"
            onClick={() => save()}
            disabled={saving}
            data-testid="cow-save-moovelous"
          >
            <Save size={14} />
            {saving ? "Saving\u2026" : "Save"}
          </button>
        </section>
      </div>
    </div>
  );
}
